import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Card from '../../components/Card.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';

const INTEGRATIONS = ['telephony', 'mailbox', 'calendar', 'invoicing', 'agent', 'webhooks'];

/**
 * Connector registry. The switches persist through the settings API, so IT and
 * administration always see the same state. Nothing here calls an external
 * service yet - flipping one records the intent and lights the badge.
 */
const IntegrationsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const toMessage = useApiError();

  const { data: settings, loading, setData } = useApi(() => settingsApi.get(), []);
  const canEdit = user.role === 'ADMIN';

  if (loading) return <Spinner full />;

  const toggle = async (key) => {
    const next = { ...settings.integrations, [key]: !settings.integrations?.[key] };
    setData({ ...settings, integrations: next });
    try {
      const saved = await settingsApi.update({ integrations: next });
      setData(saved);
    } catch (err) {
      setData(settings);
      toast.error(toMessage(err));
    }
  };

  return (
    <>
      <PageHeader title={t('it.integrations')} subtitle={t('it.integrationsHint')} />

      <div className="grid grid--3">
        {INTEGRATIONS.map((key) => {
          const on = Boolean(settings.integrations?.[key]);
          return (
            <Card key={key}>
              <div className="integration">
                <div>
                  <p className="integration__name">{t(`integrations.${key}`)}</p>
                  <p className="muted small">{t(`integrations.${key}Hint`)}</p>
                </div>
                <Badge value={on ? 'ACTIVE' : 'DISABLED'} label={on ? t('integrations.on') : t('integrations.off')} />
              </div>

              <div className="integration__actions">
                <label className={on ? 'switch switch--on' : 'switch'}>
                  <input type="checkbox" checked={on} disabled={!canEdit} onChange={() => toggle(key)} />
                  <span className="switch__track" aria-hidden="true" />
                </label>
                {!canEdit ? <span className="muted small">{t('integrations.adminOnly')}</span> : null}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default IntegrationsPage;
